<script lang="ts">
  import * as Select from '$lib/components/ui/select';
  import * as RadioButtonGroup from '$lib/components/ui/radio-button-group';
  import * as Tabs from '$lib/components/ui/tabs';
  import * as Card from '$lib/components/ui/card';
  import { Badge } from '$lib/components/ui/badge';
  import { Input } from '$lib/components/ui/input';
  import Slider from '$lib/components/ui/slider/slider.svelte';
  import RankingsRadar from './RankingsRadar.svelte';
  import RankingsTable from './RankingsTable.svelte';
  import ActivityPickerPopover from './ActivityPickerPopover.svelte';
  import HistoricalMmpPopoverV2 from '../HistoricalMmpPopoverV2.svelte';
  import Button from '$lib/components/ui/button/button.svelte';
  import * as Popover from '$lib/components/ui/popover';
  import { disciplineLabel, filterMmpCurveToRankingDurations, formatEffortDuration, isDeprecatedAthleteName, ordinalSuffix, rankingTypeFor, spelledOutDuration, valuePrecision, type DataSource, type MmpCurve, type RankingActivityOption, type RankingMode, type UserStatsResponse } from './rankingsTypes';
  import { getUserRankingStats, type HistoricalMmpDataResponse } from '@/Util/api';
  import { debouncer, round } from '@/Util/utils';
  import { Separator } from '@/lib/components/ui/separator';

  //@ts-ignore
  const Xert = window.Xert;

  export let ageFilters: Record<string, string | null> = {};
  export let genderFilters: Record<string, string | null> = {};
  export let measureUnit: string = 'W';
  export let athleteDescription: string = '';
  export let hasTrainingPlan: boolean = false;
  export let hasBreakthroughEntry: boolean = true;
  export let initialWeight: number | null = null;
  export let weightUnit: string = 'kg';
  // Overrides which endpoint stats are fetched from — omitted on the personal Rankings page
  // (defaults to the viewer's own global-population stats), passed by club/team pages to
  // compare against that club/team's members instead (see ClubController@getUserStatistics).
  export let getUserRankingStatsURL: string | undefined = undefined;

  type Option = { value: string; label: string };
  type DisciplineOption = { value: string; label: string; duration: number | null };
  const toOptions = (map: Record<string, string | number | null>): Option[] =>
    Object.entries(map).map(([label, value]) => ({ value: value == null ? '' : String(value), label }));

  const ageOptions = toOptions(ageFilters);
  const genderOptions = toOptions(genderFilters);

  let selectedAge: Option = ageOptions.find((o) => o.label === Xert.getConfig?.('age_group')) ?? ageOptions[0] ?? { value: '', label: 'All Ages' };
  let selectedGender: Option = genderOptions.find((o) => o.label === Xert.getConfig?.('gender_group')) ?? genderOptions[0] ?? { value: '', label: 'All Genders' };

  let rankingMode: RankingMode = (Xert.getConfig?.('ranking_mode') as RankingMode) ?? 'mixed';
  let grade: number = Number(Xert.getConfig?.('ranking_grade') ?? 0);

  $: rankingType = rankingTypeFor(rankingMode, grade);

  // Weight only affects relative-power calculations — Watts mode is weight-independent,
  // so there's nothing for an override to change there.
  $: showWeightInput = rankingMode === 'kge1' || rankingMode === 'mixed' || rankingMode === 'grade';
  let weightInput: string = initialWeight != null ? String(initialWeight) : '';

  // "Latest Breakthrough" is hidden entirely (and stops being the default) if the user has
  // no breakthrough activity — they'd have no entry in the population sets, so this source
  // would just show a misleading 0/bottom-percentile instead of "no data".
  const dataSourceOptions: Option[] = [
    ...(hasBreakthroughEntry ? [{ value: 'breakthrough', label: 'Latest Breakthrough' }] : []),
    ...(hasBreakthroughEntry ? [{ value: 'best_signature', label: 'Best Signature' }] : []),
    { value: 'current_signature', label: 'Current Signature' },
    ...(hasTrainingPlan ? [{ value: 'starting_signature', label: 'Starting Signature' }] : []),
    ...(hasTrainingPlan ? [{ value: 'final_signature', label: 'Final Signature' }] : []),
    { value: 'activity', label: 'Specific Activity' },
    { value: 'historical_mmp', label: 'Power Curve MMP' },
  ];
  let selectedDataSourceOption: Option = dataSourceOptions[0];
  $: dataSource = selectedDataSourceOption.value as DataSource;
  let selectedActivity: RankingActivityOption | null = null;
  let historicalMmpCurve: MmpCurve | null = null;
  // Don't fire the "activity"/"historical_mmp" sources until they're actually configured —
  // fall back to the default so the request never goes out half-set-up.
  $: fallbackDataSource = hasBreakthroughEntry ? 'breakthrough' : 'current_signature';
  $: effectiveDataSource =
    (dataSource === 'activity' && !selectedActivity) || (dataSource === 'historical_mmp' && !historicalMmpCurve)
      ? fallbackDataSource
      : dataSource;

  function persist() {
    Xert.setConfig?.('age_group', selectedAge.label);
    Xert.setConfig?.('gender_group', selectedGender.label);
    Xert.setConfig?.('ranking_mode', rankingMode);
    Xert.setConfig?.('ranking_grade', grade);
  }

  let stats: UserStatsResponse | null = null;
  let loading = false;
  const fetchDebouncer = debouncer(250);

  $: fetchKey = JSON.stringify([
    selectedAge.value,
    selectedGender.value,
    rankingType,
    effectiveDataSource,
    selectedActivity?.path ?? null,
    historicalMmpCurve,
    showWeightInput ? weightInput : null,
  ]);
  let lastFetchedKey: string | null = null;
  $: if (fetchKey !== lastFetchedKey) {
    lastFetchedKey = fetchKey;
    persist();
    fetchDebouncer.debounce(fetchStats);
  }

  async function fetchStats() {
    loading = true;
    try {
      stats = await getUserRankingStats(
        { age: selectedAge.value || undefined, gender: selectedGender.value || undefined },
        rankingType,
        effectiveDataSource,
        selectedActivity?.path,
        showWeightInput ? weightInput : undefined,
        historicalMmpCurve ?? undefined,
        getUserRankingStatsURL,
      );
    } finally {
      loading = false;
    }
  }

  function handleHistoricalMmpLoad(data: HistoricalMmpDataResponse) {
    historicalMmpCurve = filterMmpCurveToRankingDurations(data);
  }

  const modeLabels: Record<RankingMode, string> = { mixed: 'Mixed', kge0: 'Watts', kge1: 'W/Kg', grade: 'Grade' };

  // Which discipline's numbers the stat tiles show — defaults to the user's own
  // athlete type, but the dropdown below lets them browse any other discipline.
  // Not persisted: a fresh page load always starts back at the default.
  $: disciplineOptions = (stats?.radar ?? []).map((r) => ({ value: r.db_name, label: disciplineLabel(r.athlete), duration: r.duration }));
  let selectedDisciplineOption: DisciplineOption | null = null;
  $: if (stats && !selectedDisciplineOption) {
    selectedDisciplineOption = disciplineOptions.find((o) => o.value === stats?.user_athlete_db) ?? disciplineOptions[0] ?? null;
  }

  // The radar array already carries everything about the selected discipline (current,
  // unit, percentile, duration) for the selected ranking type — reuse that single
  // entry instead of re-deriving the same numbers a second way.
  $: currentEntry = stats?.radar.find((r) => r.db_name === selectedDisciplineOption?.value) ?? null;

  // Grade mode adjusts raw power for gradient, so "W" alone is misleading there —
  // call it out as "Relative Power" instead of the bare unit.
  $: powerWord = rankingMode === 'grade' ? 'Relative Power' : currentEntry?.unit === 'W/kg' ? 'W/kg' : 'Power';

  // Duration + unit depend on the selected ranking type, so once we have a match,
  // prefer that over the static description passed in from the blade view (used
  // only for the first paint before any fetch resolves). The deprecated hour-power
  // names already spell out their own duration, so no separate description needed.
  $: displayedDescription = currentEntry && !isDeprecatedAthleteName(currentEntry.athlete) ? `${spelledOutDuration(currentEntry.duration)} ${powerWord}` : '';
</script>

<div class="flex flex-col gap-4">
  <!-- Filter row: scopes everything below it -->
  <div class="flex flex-wrap items-center gap-3 bg-background p-3 rounded-md border border-border">
    <Select.Root portal="#shadcn-ui-dialog-target" items={ageOptions} bind:selected={selectedAge}>
      <Select.Trigger class="w-36"><Select.Value placeholder="Age" /></Select.Trigger>
      <Select.Content>
        {#each ageOptions as o}<Select.Item value={o.value}>{o.label}</Select.Item>{/each}
      </Select.Content>
    </Select.Root>

    <Select.Root portal="#shadcn-ui-dialog-target" items={genderOptions} bind:selected={selectedGender}>
      <Select.Trigger class="w-36"><Select.Value placeholder="Gender" /></Select.Trigger>
      <Select.Content>
        {#each genderOptions as o}<Select.Item value={o.value}>{o.label}</Select.Item>{/each}
      </Select.Content>
    </Select.Root>

    <Select.Root portal="#shadcn-ui-dialog-target" items={dataSourceOptions} bind:selected={selectedDataSourceOption}>
      <Select.Trigger class="w-44"><Select.Value placeholder="Compare using" /></Select.Trigger>
      <Select.Content>
        {#each dataSourceOptions as o}<Select.Item value={o.value}>{o.label}</Select.Item>{/each}
      </Select.Content>
    </Select.Root>
    {#if dataSource === 'activity'}
      <ActivityPickerPopover selected={selectedActivity} onSelect={(a) => (selectedActivity = a)} />
    {:else if dataSource === 'historical_mmp'}
      <HistoricalMmpPopoverV2
        hasData={!!historicalMmpCurve}
        onLoad={handleHistoricalMmpLoad}
        onClear={() => (historicalMmpCurve = null)}
      />
    {/if}

    <div class="flex flex-wrap items-center gap-3 ml-auto">
      {#if showWeightInput}
        <div class="flex items-center gap-2">
          <span class="text-sm text-muted-foreground">Weight</span>
          <Input type="number" step="0.1" min="0" class="w-20" bind:value={weightInput} />
          <span class="text-sm text-muted-foreground">{weightUnit}</span>
        </div>
        <Separator orientation='vertical' class='h-8' />
      {/if}
      
      {#if rankingMode === 'grade'}
        <div class="flex items-center gap-2 min-w-[180px]">
          <Slider min={-2} max={10} step={1} value={[grade]} onValueChange={(v) => (grade = v[0])} />
          <span class="text-sm font-medium tabular-nums w-10 shrink-0 text-right">{grade > 0 ? `+${grade}` : grade}% grade</span>
        </div>
        <Separator orientation='vertical' class='h-8' />
      {/if}

      <RadioButtonGroup.Root selectVariant="primary" size="sm" grouped bind:value={rankingMode}>
        {#each Object.entries(modeLabels) as [value, label]}
          <RadioButtonGroup.Button {value} class="[&:not(:has(button[data-state='checked']))]:bg-muted">{label}</RadioButtonGroup.Button>
        {/each}
      </RadioButtonGroup.Root>

      <Popover.Root portal="#shadcn-ui-dialog-target">
        <Popover.Trigger asChild let:builder>
          <Button builders={[builder]} variant="outline" size="icon" class="h-5 w-5 shrink-0 rounded-full p-0 text-xs text-muted-foreground">
            ?
          </Button>
        </Popover.Trigger>
        <Popover.Content class="w-80 text-xs leading-relaxed" align="start">
          <p class="mb-2 font-semibold text-foreground">How ranking modes work</p>
          <p class="mb-2 text-muted-foreground">
            Watts compares raw power, W/Kg compares power per kilogram of body weight, and Mixed
            picks whichever is standard for each discipline.
          </p>
          <p class="mb-2 font-semibold text-foreground">How relative power is calculated</p>
          <p class="mb-2 text-muted-foreground">
            <span class="font-medium text-foreground">Grade</span> ranks you on
            <span class="font-medium text-foreground">Relative Power</span> — power re-expressed as
            what a <span class="font-medium text-foreground">75&nbsp;kg reference rider on a 9&nbsp;kg bike</span> would
            need to ride at the same speed on the chosen gradient.
          </p>
          <p class="mb-2 text-muted-foreground">
            Your power is scaled by the mass ratio of the reference rider to your
            <span class="font-medium text-foreground">weight</span> (rider&nbsp;+&nbsp;bike), raised
            to the <span class="font-medium text-foreground">KGE</span> exponent:
          </p>
          <p class="mb-2 rounded bg-muted/50 px-2 py-1 text-center font-mono text-foreground">
            P<sub>rel</sub> = P × ((75 + 9) / (w + 9))<sup>KGE</sup>
          </p>
          <p class="text-muted-foreground">
            The <span class="font-medium text-foreground">grade</span> sets how steep the climb is
            and converts into the KGE exponent: on the flat, aerodynamic drag dominates and KGE is
            small; as the grade increases, gravity dominates and KGE approaches&nbsp;1, so heavier
            riders are penalized more.
          </p>
        </Popover.Content>
      </Popover.Root>
    </div>
  </div>

  <!-- Personal + population stat tiles -->
  {#if selectedDisciplineOption}
    <div class="flex items-baseline gap-1.5 text-lg font-semibold">
      <Select.Root portal="#shadcn-ui-dialog-target" items={disciplineOptions} bind:selected={selectedDisciplineOption}>
        <Select.Trigger class="h-auto w-auto gap-1 border-none bg-transparent p-0 text-lg font-semibold shadow-none focus:ring-0 focus-visible:ring-0">
          <Select.Value />
        </Select.Trigger>
        <Select.Content class="min-w-[16rem]">
          {#each disciplineOptions as o}
            <Select.Item value={o.value} label={o.label}>
              <span class="flex w-full items-center justify-between gap-4">
                <span class="whitespace-nowrap">{o.label}</span>
                <span class="whitespace-nowrap text-xs text-muted-foreground">{formatEffortDuration(o.duration)}</span>
              </span>
            </Select.Item>
          {/each}
        </Select.Content>
      </Select.Root>
      {#if displayedDescription}<span class="font-normal text-muted-foreground">{displayedDescription}</span>{/if}
    </div>
  {:else if athleteDescription}
    <div class="text-lg font-semibold">{athleteDescription}</div>
  {/if}
  <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
    <Card.Root>
      <Card.Content class="p-4">
        <div class="text-sm text-muted-foreground">Current</div>
        <div class="flex items-center justify-between gap-2">
          <div class="text-2xl font-semibold">
            {currentEntry ? round(currentEntry.current, valuePrecision(currentEntry.unit)) : stats ? stats.current : '—'}
            <span class="text-sm text-muted-foreground font-normal">{currentEntry?.unit ?? stats?.pop_unit ?? measureUnit}</span>
          </div>
          {#if currentEntry}<Badge variant="secondary" class="shrink-0 font-medium">{round(currentEntry.percentile)}{ordinalSuffix(currentEntry.percentile)} Percentile</Badge>{/if}
        </div>
      </Card.Content>
    </Card.Root>
    <Card.Root>
      <Card.Content class="p-4">
        <div class="text-sm text-muted-foreground">Population median</div>
        <div class="text-2xl font-semibold">{currentEntry ? round(currentEntry.median, valuePrecision(currentEntry.unit)) : stats ? round(stats.pop_avg, valuePrecision(stats.pop_unit)) : '—'} <span class="text-sm text-muted-foreground font-normal">{currentEntry?.unit ?? stats?.pop_unit ?? 'W'}</span></div>
      </Card.Content>
    </Card.Root>
    <Card.Root>
      <Card.Content class="p-4">
        <div class="text-sm text-muted-foreground">90th percentile</div>
        <div class="text-2xl font-semibold">{currentEntry ? round(currentEntry.best, valuePrecision(currentEntry.unit)) : stats ? round(stats.pop_max, valuePrecision(stats.pop_unit)) : '—'} <span class="text-sm text-muted-foreground font-normal">{currentEntry?.unit ?? stats?.pop_unit ?? 'W'}</span></div>
      </Card.Content>
    </Card.Root>
  </div>

  <!-- Radar / table -->
  <div class="bg-background rounded-md border border-border p-4" class:opacity-60={loading}>
    <div class="mb-2">
      <div class="text-lg font-semibold">Rankings</div>
      <div class="text-sm text-muted-foreground">Percentile across all disciplines</div>
    </div>

    {#if stats?.radar?.length}
      <Tabs.Root value="radar">
        <Tabs.List class="grid grid-cols-2 w-48 mb-3">
          <Tabs.Trigger value="radar">Radar</Tabs.Trigger>
          <Tabs.Trigger value="table">Table</Tabs.Trigger>
        </Tabs.List>
        <Tabs.Content value="radar">
          <RankingsRadar entries={stats.radar} />
        </Tabs.Content>
        <Tabs.Content value="table">
          <RankingsTable entries={stats.radar} />
        </Tabs.Content>
      </Tabs.Root>
    {:else}
      <div class="text-sm text-muted-foreground py-12 text-center">
        {loading ? 'Loading…' : 'No ranking data yet for this selection.'}
      </div>
    {/if}
  </div>
</div>
